/**
 * Embed widget para iframe-ar desde CRMs partner.
 *
 * Misma lógica del pagador pero sin chrome (sin header de marca):
 * pensado para meterse en un iframe de ~400×600. Notifica al parent por
 * postMessage cuando carga y cuando se completa el join.
 */
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import api from '../api/client';
import { copyFor, progressPhrase } from '../utils/vertical-copy';


interface GroupBuyPublic {
  slug: string;
  type: string;
  title: string;
  organizer_name: string;
  amount_per_share?: string;
  expected_shares?: number;
  status: string;
  paid_shares_count: number;
  total_paid_amount: string;
  require_dni: boolean;
}


function formatEUR(n: number): string {
  return n.toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
}


export default function GroupBuyEmbedPage() {
  const { slug } = useParams<{ slug: string }>();
  const [gb, setGb] = useState<GroupBuyPublic | null>(null);
  const [loading, setLoading] = useState(true);

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [dni, setDni] = useState('');
  const [shares, setShares] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [method, setMethod] = useState<'stripe' | 'bizum'>('stripe');

  useEffect(() => {
    if (!slug) return;
    api.get(`/public/group-buys/${slug}`)
      .then((r) => setGb(r.data))
      .catch(() => setError('No encontramos este cobro'))
      .finally(() => setLoading(false));

    try {
      window.parent.postMessage({ type: 'cc:embed:loaded', slug }, '*');
    } catch {}
  }, [slug]);

  const handleJoin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!name || !email) {
      setError('Pon tu nombre y email');
      return;
    }
    if (gb?.require_dni && !dni) {
      setError('Necesitas el DNI para que la papeleta quede a tu nombre');
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post(`/public/group-buys/${slug}/join`, {
        participant_name: name,
        participant_email: email,
        participant_dni: dni || undefined,
        shares_count: shares,
        payment_method: method,
      });

      try {
        window.parent.postMessage(
          {
            type: 'cc:embed:join',
            slug,
            order_id: data.ticket_order_id,
            reference: data.payment_reference,
          },
          '*',
        );
      } catch {}

      window.top!.location.href = data.payment_url;
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Algo no ha ido bien. Inténtalo de nuevo.');
      setSubmitting(false);
    }
  };

  if (loading)
    return (
      <div className="p-6 text-center text-ink-500 font-sans bg-paper min-h-screen">
        Un momento…
      </div>
    );
  if (error && !gb)
    return (
      <div className="p-6 text-center text-brick-700 font-sans bg-paper min-h-screen">
        {error}
      </div>
    );
  if (!gb) return null;

  const c = copyFor(gb.type);
  const amount = gb.amount_per_share ? parseFloat(gb.amount_per_share) : 0;
  const total = amount * shares;
  const isOpen = gb.status === 'open';
  const progress = gb.expected_shares
    ? Math.min(100, (gb.paid_shares_count / gb.expected_shares) * 100)
    : 0;
  const maxShares = 50;

  return (
    <div className="min-h-screen bg-paper text-ink font-sans antialiased">
      <div className="max-w-md mx-auto px-4 py-5">
        {/* Eyebrow + título */}
        <div className="font-mono text-[10.5px] uppercase tracking-[0.18em] text-ink-500 mb-1.5">
          · {c.shortLabel}
        </div>
        <h1 className="font-display font-bold text-[20px] text-ink leading-tight tracking-[-0.01em]">
          {gb.title}
        </h1>
        <p className="text-[12px] text-ink-500 mt-0.5">
          Organiza: <span className="text-ink-700">{gb.organizer_name}</span>
        </p>

        {/* Progreso */}
        {gb.expected_shares ? (
          <div className="mt-4">
            <div className="flex justify-between text-[12px] text-ink-700 mb-1.5">
              <span>{progressPhrase(gb.type, gb.paid_shares_count, gb.expected_shares)}</span>
              <span className="font-semibold text-olive-700">
                {formatEUR(parseFloat(gb.total_paid_amount))}
              </span>
            </div>
            <div className="h-1.5 bg-line rounded-pill overflow-hidden">
              <div
                className="h-full bg-olive rounded-pill"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>
        ) : null}

        {!isOpen ? (
          <div className="mt-5 bg-amberCC-50 border border-[#ECD9AE] rounded-xl p-3 text-[13px] text-amberCC-700">
            Este cobro ya no acepta pagos ({gb.status}).
          </div>
        ) : (
          <form onSubmit={handleJoin} className="mt-5 space-y-2.5">
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Tu nombre"
              required
              className="cc-input"
            />
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Email"
              required
              className="cc-input"
            />
            {gb.require_dni && (
              <input
                type="text"
                value={dni}
                onChange={(e) => setDni(e.target.value)}
                placeholder="DNI (validez legal)"
                required
                className="cc-input"
              />
            )}

            {/* Stepper compacto */}
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setShares((s) => Math.max(1, s - 1))}
                className="w-10 h-10 rounded-xl border-[1.5px] border-line-strong bg-paper text-ink-700 text-lg font-bold hover:border-brand transition disabled:opacity-50"
                disabled={shares <= 1}
                aria-label="Quitar una"
              >
                −
              </button>
              <div className="flex-1 h-10 rounded-xl border-[1.5px] border-line-strong bg-paper flex items-center justify-center font-display font-semibold text-[17px] text-ink">
                {shares}
              </div>
              <button
                type="button"
                onClick={() => setShares((s) => Math.min(maxShares, s + 1))}
                className="w-10 h-10 rounded-xl border-[1.5px] border-line-strong bg-paper text-ink-700 text-lg font-bold hover:border-brand transition disabled:opacity-50"
                disabled={shares >= maxShares}
                aria-label="Sumar una"
              >
                +
              </button>
              <div className="px-3 py-2 font-display font-bold text-[16px] text-ink whitespace-nowrap">
                {formatEUR(total)}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-2">
              <SegBtn selected={method === 'stripe'} onClick={() => setMethod('stripe')}>
                💳 Tarjeta
              </SegBtn>
              <SegBtn selected={method === 'bizum'} onClick={() => setMethod('bizum')}>
                📱 Bizum
              </SegBtn>
            </div>

            {error && (
              <div className="bg-brick-50 border border-[#E6B8B0] rounded-xl px-3 py-2 text-[12.5px] text-brick-700">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={submitting}
              className="w-full px-4 py-3 rounded-xl bg-brand text-paper font-semibold text-[14px] shadow-btn hover:bg-brand-700 transition disabled:bg-[#EFE8DC] disabled:text-[#B2A89A] disabled:shadow-none disabled:cursor-not-allowed"
            >
              {submitting ? 'Un momento…' : `Pagar ${formatEUR(total)}`}
            </button>
            <p className="font-mono text-[10px] uppercase tracking-[0.14em] text-ink-500 text-center">
              Sin coste para ti · Justificante por email
            </p>
          </form>
        )}
      </div>

      <Style />
    </div>
  );
}


function SegBtn({
  selected,
  onClick,
  children,
}: {
  selected: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={
        selected
          ? 'px-2 py-2 rounded-xl text-[12.5px] font-semibold border-[1.5px] border-brand bg-brand-50 text-brand-700 transition'
          : 'px-2 py-2 rounded-xl text-[12.5px] font-semibold border-[1.5px] border-line-strong bg-paper text-ink-700 hover:border-brand transition'
      }
    >
      {children}
    </button>
  );
}


function Style() {
  return (
    <style>{`
      .cc-input{
        width:100%;
        padding:10px 13px;
        border:1.5px solid #DDD3C4;
        border-radius:11px;
        background:#FFFFFF;
        font-family:inherit;
        font-size:13.5px;
        color:#26201A;
        outline:none;
        transition:border-color .15s ease;
      }
      .cc-input:focus{ border-color:#1C5D82; }
      .cc-input::placeholder{ color:#A89D8E; }
    `}</style>
  );
}
